import "./insRangeChart.scss";
import { AuxBadge } from "@blk/aladdin-react-components-es";
import { AuxBadgeStyleEnum, AuxBadgeTypeEnum } from "@blk/aladdin-web-components";
import Highcharts, { AST } from "highcharts/highstock";
import noDataModule from "highcharts/modules/no-data-to-display";
import xRangeModule from "highcharts/modules/xrange";
import React, { useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { formatUnPrinciplesAxisLabels, FormatUnPrinciplesTooltip, QuarterAxisLabelsFormatter, YearAxisLabelsFormatter } from "./insRangeChartFormatters";
import { getUnPrinciplesValues } from "./un-principles-mapper";
import { ViolationType } from "common/un-principles-helper";
import { InsHighcharts } from "components";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { useGetDatapointsWidgetData } from "hooks/widget-data/use-datapoints";
import { language } from "language/default";
xRangeModule(Highcharts);
noDataModule(Highcharts);
// This is due to a new security feature that came up with highcharts 9
AST.allowedTags.push("aux-badge");
AST.allowedAttributes.push("badge-style");
AST.allowedAttributes.push("type");
const principlesText = language.en.widgets.principlesTable;
// eslint-disable-next-line no-magic-numbers
const oneYear = 365 * 24 * 3600 * 1000;
// eslint-disable-next-line no-magic-numbers
const halfQuarter = 45 * 24 * 3600 * 1000;
const axisMargin = 40;
const getTicks = (min, max) => {
    const quarters = [], years = [];
    const current = new Date(min);
    const limit = new Date(max);
    while (current.getTime() < limit.getTime()) {
        if (current.getUTCMonth() === 0) {
            years.push(current.getTime());
        }
        if (current.getUTCMonth() % 3 === 0) {
            quarters.push(current.getTime() + halfQuarter);
        }
        current.setUTCMonth(current.getUTCMonth() + 1);
    }
    return { quarters, years };
};
export const InsRangeChart = (props) => {
    const { sectionId, widgetId } = props;
    const [[result], queryState] = useGetDatapointsWidgetData(props);
    const series = getUnPrinciplesValues(result?.data?.[0]?.data?.[0]?.data?.map((x) => x.measures));
    const { start, end, noViolations, violations, potential } = useMemo(() => ({
        end: Math.max(...series.data.map((d) => d.x2 || 0)),
        noViolations: series.data.every((d) => d.custom?.type === ViolationType[ViolationType.NoViolation]),
        potential: series.data.filter((d) => d.custom?.type === ViolationType[ViolationType.Potential]),
        start: Math.min(...series.data.map((d) => d.x || Number.MAX_VALUE)),
        violations: series.data.filter((d) => d.custom?.type === ViolationType[ViolationType.Violation])
    }), [series.data]);
    const { years, quarters } = useMemo(() => getTicks(start, end), [start, end]);
    const options = {
        chart: { className: `insRangeChart${noViolations ? " noViolations" : ""}`, styledMode: true },
        credits: { enabled: false },
        lang: {
            /*
             * We are adding { badge-style: success } instead of badgStyle due to a bug where AuxBadge isn't set up correclty when it is "detached" like this.
             * Also badgeStyle doesn't work with highcharts 9 (attributes with capital letters cannot be added to AST)
             */
            noData: `${renderToStaticMarkup(React.createElement("div", { className: "icon" },
                React.createElement(AuxBadge, { type: AuxBadgeTypeEnum.ICON, "badge-style": AuxBadgeStyleEnum.SUCCESS })))}${principlesText.noViolation}`
        },
        legend: { align: "left" },
        noData: { useHTML: true },
        plotOptions: { series: { events: { legendItemClick: () => true } }, xrange: { grouping: false } },
        series: [
            { className: "violation-series", data: violations, name: principlesText.violation, type: "xrange", yAxis: 0 },
            { className: "potential-series", data: potential, name: principlesText.potentialViolation, type: "xrange", yAxis: 0 },
            { data: noViolations ? [] : [{ y: series.groups.length - 1 }], showInLegend: false, type: "xrange", yAxis: 1 }
        ],
        title: { text: "" },
        tooltip: {
            formatter: FormatUnPrinciplesTooltip
        },
        xAxis: [
            {
                labels: {
                    align: "left",
                    formatter: YearAxisLabelsFormatter,
                    y: axisMargin
                },
                max: end,
                min: start,
                minorTickInterval: oneYear,
                minorTicks: !noViolations,
                tickPositions: years,
                type: "datetime"
            },
            {
                labels: {
                    formatter: QuarterAxisLabelsFormatter
                },
                linkedTo: 0,
                max: end,
                min: start,
                offset: 0,
                tickLength: 0,
                tickPositions: quarters,
                type: "datetime"
            }
        ],
        yAxis: [
            {
                categories: series.principles,
                labels: {
                    align: "left",
                    formatter: formatUnPrinciplesAxisLabels,
                    reserveSpace: true,
                    useHTML: true
                },
                max: series.principles.length - 1,
                min: 0,
                reversed: true,
                title: { text: null }
            },
            {
                categories: series.groups,
                className: "violations-groups",
                labels: { align: "left", reserveSpace: true },
                margin: -axisMargin,
                max: series.principles.length - 1,
                min: 0,
                reversed: true,
                title: { text: null }
            }
        ]
    };
    return React.createElement(InsQueryStatedElement, { widgetFactoryProps: props, queryState: queryState, hasData: series.data.length > 0 },
        React.createElement(InsHighcharts, { options: options, sectionId: sectionId, widgetId: widgetId, isMaximizedOne: props.isMaximizedOne }));
};
